/**
 * Placement maths for the floating help bubble.
 *
 * The bubble is portalled to <body> and positioned `fixed`, so it escapes the
 * modal it is opened from — but that also means nothing clips it back onto the
 * screen. Everything that keeps it inside the viewport lives here, and is kept
 * free of the DOM so it can be tested directly.
 */

// breathing room between the bubble and the edge of the screen
export const HELP_BUBBLE_EDGE_MARGIN = 8;
// gap between the anchor icon and the bubble
export const HELP_BUBBLE_ANCHOR_GAP = 8;
// wide enough for a sentence or two, narrow enough to stay readable
export const HELP_BUBBLE_MAX_WIDTH = 320;
// how far the caret must stay from the bubble's rounded corners
const CARET_INSET = 14;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

export const getHelpBubbleWidth = viewportWidth =>
  Math.max(
    0,
    Math.min(HELP_BUBBLE_MAX_WIDTH, viewportWidth - HELP_BUBBLE_EDGE_MARGIN * 2)
  );

export const computeHelpBubblePosition = ({
  anchorRect,
  bubbleHeight = 0,
  viewportWidth,
  viewportHeight,
}) => {
  const width = getHelpBubbleWidth(viewportWidth);
  const anchorCentre = anchorRect.left + anchorRect.width / 2;

  // centre on the anchor, then push back inside whichever edge it crossed
  const left = clamp(
    anchorCentre - width / 2,
    HELP_BUBBLE_EDGE_MARGIN,
    viewportWidth - HELP_BUBBLE_EDGE_MARGIN - width
  );

  const topIfBelow = anchorRect.bottom + HELP_BUBBLE_ANCHOR_GAP;
  const topIfAbove = anchorRect.top - HELP_BUBBLE_ANCHOR_GAP - bubbleHeight;
  const fitsBelow =
    topIfBelow + bubbleHeight + HELP_BUBBLE_EDGE_MARGIN <= viewportHeight;
  const fitsAbove = topIfAbove >= HELP_BUBBLE_EDGE_MARGIN;

  // below by default; flip up only when that is genuinely the better fit
  const placement = !fitsBelow && fitsAbove ? 'above' : 'below';
  const top = placement === 'above' ? topIfAbove : topIfBelow;

  return {
    left,
    top,
    width,
    placement,
    // the caret follows the anchor, but never slides off the bubble itself
    caretLeft: clamp(anchorCentre - left, CARET_INSET, width - CARET_INSET),
  };
};
